import React, { useEffect, useRef } from 'react';
import { useNavigation } from '@react-navigation/native';
import { View } from 'react-native';
import { Color, CommonStyles } from '../../styles';
import Button from '../Button';
import ExpenseFormFields from './ExpenseFormFields';
import { Modals } from '../../constants/ScreenNames';
import { useDispatch, useSelector } from 'react-redux';
import {
  initiateExpenseValues,
  setCurrentReportIdForUpdate,
  setMultipleExpenseValues,
} from '../../store/actions/expense';
import {
  filterPendingReports,
  isNullOrEmpty,
  removeObjectEmptyValues,
} from '../../utils/common.utils';
import {
  selectCreditCards,
  selectExpenseList,
  selectExpenseStates,
} from '../../store/selectors/expense.selector';
import {
  ExpenseCategories,
  DEFAULT_SELECT_SUBCATEGORY,
  DynamoDBFieldNames,
  MileageCategories,
  DEFAULT_MILEAGE_SUBCATEGORY,
} from '../../constants/ExpenseCategories';
import {
  EXPENSE_ACTION_TYPES,
  EXPENSE_FORM_KEYS,
  EXPENSE_SUB_TAB_OPTIONS,
} from '../../constants/Enums';
import useExpenseFormValidation from '../../utils/useExpenseFormValidation';
import {
  applyFieldTranslations,
  applyReceiptsTranslation,
  extractFormFieldDefaults,
  getSubcategoryById,
  initializeBusinessChargeValues,
  initializeExpenseState,
  shouldUpdateExpenseState,
} from '../../utils/expense.utils';
import ReceiptsBar from './ReceiptsBar';
import ExpenseToggles from './ExpenseToggles';

const useExpenseFormStepOne = ({
  isCreationForm,
  subTab = null,
  isEditable = true,
  selectedCharge = null,
  existingExpense = null,
  fromExistingReport = false,
  fromReceiptsTab = false,
  selectedReceipts = [],
  receiptsData = null,
  totalCharges = null,
  currentChargeIndex = null,
  continueButtonStyle = null,
  isMileage = false,
  firstExpenseReportId = null,
}) => {
  const dispatch = useDispatch();
  const navigation = useNavigation();
  const actionType = isCreationForm ? EXPENSE_ACTION_TYPES.CREATION : EXPENSE_ACTION_TYPES.UPDATE;
  const isContinueDisabled = !useExpenseFormValidation({
    step: EXPENSE_FORM_KEYS.STEP_ONE,
    actionType,
    subTab,
  });
  const expenseStates = useSelector(selectExpenseStates)[actionType][subTab];
  const expenseList = useSelector(selectExpenseList);
  const creditCardsList = useSelector(selectCreditCards);
  const lockedFieldsRef = useRef([]);
  const currentSelectedSubCategory = expenseStates[DynamoDBFieldNames.SELECTED_SUBCATEGORY];

  // Initialize empty form with default values if no state exists
  const initializeDefaultExpenseState = () => {
    if (isNullOrEmpty(expenseStates)) {
      const formFields = initializeExpenseState(isMileage ? MileageCategories : ExpenseCategories);
      if (isNullOrEmpty(currentSelectedSubCategory) && isNullOrEmpty(existingExpense)) {
        formFields[DynamoDBFieldNames.SELECTED_SUBCATEGORY] = isMileage
          ? DEFAULT_MILEAGE_SUBCATEGORY
          : DEFAULT_SELECT_SUBCATEGORY;
      }
      dispatch(initiateExpenseValues({ actionType, subTab, formFields }));
    }
  };

  // When a charge is selected as business, populate and lock relevant fields
  const populateFormWithTheSelectedCharge = (charge) => {
    const { chargeFieldsMap, lockedFields } = initializeBusinessChargeValues(charge);
    const itemSubCategory = getSubcategoryById(charge?.SubCategoryIdSelected);
    lockedFieldsRef.current = lockedFields;
    if (!isNullOrEmpty(chargeFieldsMap)) {
      dispatch(
        setMultipleExpenseValues({
          updatedFields: {
            ...applyFieldTranslations(chargeFieldsMap, creditCardsList),
            [DynamoDBFieldNames.SELECTED_SUBCATEGORY]: itemSubCategory,
          },
          actionType,
          subTab,
        })
      );
    }
  };

  // Special handling for editing expenses that were created from charges
  const populateExistingExpenseWithCharge = (existingExpense, itemSubCategory) => {
    const { chargeFieldsMap, lockedFields } = initializeBusinessChargeValues(
      existingExpense.Charge
    );
    lockedFieldsRef.current = lockedFields;

    const nonEmptyChargeValues = removeObjectEmptyValues(chargeFieldsMap);

    dispatch(
      setMultipleExpenseValues({
        updatedFields: {
          ...extractFormFieldDefaults(itemSubCategory),
          ...applyFieldTranslations(existingExpense, creditCardsList),
          ...nonEmptyChargeValues,
          receipts: applyReceiptsTranslation(existingExpense?.Receipts),
          [DynamoDBFieldNames.SELECTED_SUBCATEGORY]: itemSubCategory,
        },
        actionType,
        subTab,
      })
    );
  };

  // When editing an existing expense
  const populateFormWithExistingExpense = (existingExpense) => {
    // Set the category from the existing expense
    const itemSubCategory = getSubcategoryById(
      existingExpense.ExpenseSubCategory,
      isMileage ? MileageCategories : ExpenseCategories
    );

    // If the existing expense has an associated charge, handle it specially
    if (!isNullOrEmpty(existingExpense?.Charge)) {
      populateExistingExpenseWithCharge(existingExpense, itemSubCategory);
      return;
    }

    // Otherwise just populate with the expense data
    dispatch(
      setMultipleExpenseValues({
        updatedFields: {
          // Populate default values for subcategory form fields, accounting for changes in form fields that may be missing in older expenses
          ...extractFormFieldDefaults(itemSubCategory),
          ...applyFieldTranslations(existingExpense, creditCardsList),
          receipts: applyReceiptsTranslation(existingExpense?.Receipts),
          [DynamoDBFieldNames.SELECTED_SUBCATEGORY]: itemSubCategory,
        },
        actionType,
        subTab,
      })
    );
  };

  const populateFormWithReceiptsData = (receiptsData, selectedReceipts) => {
    dispatch(
      setMultipleExpenseValues({
        updatedFields: {
          ...extractFormFieldDefaults(DEFAULT_SELECT_SUBCATEGORY),
          ...applyFieldTranslations(receiptsData, creditCardsList),
          receipts: applyReceiptsTranslation(selectedReceipts),
          [DynamoDBFieldNames.SELECTED_SUBCATEGORY]: DEFAULT_SELECT_SUBCATEGORY,
        },
        actionType,
        subTab,
      })
    );
  };

  useEffect(() => {
    // 1. Initialize form if needed
    initializeDefaultExpenseState();

    // 2. If a charge is selected as business, populate form with charge data
    if (!isNullOrEmpty(selectedCharge)) {
      populateFormWithTheSelectedCharge(selectedCharge);
      return;
    }

    // 3. If editing an existing expense, populate with its data
    if (!isNullOrEmpty(existingExpense)) {
      populateFormWithExistingExpense(existingExpense);
      return;
    }

    // 4. If coming from receipts subtab
    if (fromReceiptsTab) {
      populateFormWithReceiptsData(receiptsData, selectedReceipts);
      return;
    }
  }, [selectedCharge, existingExpense, fromReceiptsTab, receiptsData, selectedReceipts]);

  useEffect(() => {
    const filteresReportsList = filterPendingReports(expenseList);
    const reportsLength = filteresReportsList?.length;
    if (isNullOrEmpty(reportsLength) || reportsLength === 0) {
      return;
    }
    dispatch(setCurrentReportIdForUpdate(filteresReportsList[0]));
  }, [expenseList]);

  const onContinue = () => {
    navigation.navigate(Modals.ExpenseFormStepTwo, {
      fromExistingReport,
      isCreationForm,
      subTab,
      isEditable,
      selectedCharge,
      lockedFields: lockedFieldsRef.current,
      totalCharges,
      currentChargeIndex,
      isMileage,
      firstExpenseReportId,
    });
  };

  const stepOneForm = (
    <View style={CommonStyles.flex1}>
      {!isMileage && subTab !== EXPENSE_SUB_TAB_OPTIONS.MANUAL ? (
        <ReceiptsBar
          isEditable={isEditable}
          actionType={actionType}
          subTab={subTab}
          // OCR won't update values if it's a charge expense
          updateExpenseState={shouldUpdateExpenseState(isCreationForm, selectedCharge)}
        />
      ) : null}
      <ExpenseToggles actionType={actionType} subTab={subTab} isEditable={isEditable} />
      <ExpenseFormFields
        isEditable={isEditable}
        step={EXPENSE_FORM_KEYS.STEP_ONE}
        lockedFields={lockedFieldsRef.current}
        isCreationForm={isCreationForm}
        subTab={subTab}
        fromExistingReport={fromExistingReport}
        key={selectedCharge?.TransactionId} // Add key to force re-render when charge changes
        isMileage={isMileage}
      />
    </View>
  );

  const stepOneContinueButton = (
    <Button
      label="Continue"
      style={[
        continueButtonStyle,
        isContinueDisabled && {
          backgroundColor: Color.lightgray,
        },
      ]}
      labelStyle={[
        CommonStyles.buttonFont,
        isContinueDisabled && {
          color: Color.disabledText,
        },
      ]}
      onPress={onContinue}
      disabled={isContinueDisabled}
    />
  );

  return { stepOneForm, stepOneContinueButton };
};

export default useExpenseFormStepOne;
